"use client";

import { useEffect } from "react";
import { useStatsigClient } from "@repo/statsig";
import { useInitialContext, type DefaultInitialContext } from "@repo/sdk";
import { useStatsigUserProperties } from "@repo/statsig";
import { BRAND } from "@repo/tailwind-config/brand";
import { useSubscriptionCheck } from "src/lib/subscription-gate";
import { filterChildrenUnder21 } from "src/lib/member-utils";
import {
  mapSubscriptionStatusToTaxonomy,
  cancelsAtPeriodEnd,
} from "src/lib/subscription-status";
import { useTrustedUserEmail } from "src/lib/owlet-trusted-email";

/**
 * Pushes slow-changing user state into the Statsig user `custom` field so
 * it's available for experiment segmentation and need not be stamped on
 * every event. See `packages/statsig/TAXONOMY.md` → "User properties vs
 * event metadata".
 *
 * Mounted alongside `<StatsigObserver />` in `CommonProviders`. Renders
 * nothing — pure side effect.
 *
 * `subscription_status` mirrors Stripe's raw status. `SubscriptionState` is
 * coarse (collapses trialing into "active" and a scheduled cancel into
 * "canceling"), so trialing is recovered via the `isTrialing` flag and the
 * scheduled-cancel is written as the separate `cancels_at_period_end` property
 * (see `mapSubscriptionStatusToTaxonomy` / `cancelsAtPeriodEnd`). The webhook
 * is the authoritative source for the precise status. Phase 2 adds
 * Prisma-denormalized n_visits to this hydration path.
 */
export default function StatsigUserPropertiesSync() {
  const initialContext = useInitialContext<DefaultInitialContext | null>();
  const { subscriptionState, isTrialing } = useSubscriptionCheck();
  const { client } = useStatsigClient();
  // Owlet-only: the More menu's "Signed in as" email source. Reads the
  // *original* idToken from `window.portalInitialContext` (injected by
  // the native shell at app launch), so it survives token refresh —
  // unlike the FELT_USER_TOKENS cookie that StatsigProvider's
  // server-side boot reads, which Firebase strips `user_email` from on
  // refresh-issued tokens. On Kinsa this hook returns null (no
  // portalInitialContext), so this effect is a no-op there.
  const trustedEmail = useTrustedUserEmail();

  const account = initialContext?.account;
  const coveredPersons = account?.coveredPersons ?? [];
  const eligibleChildren = filterChildrenUnder21(coveredPersons);

  useStatsigUserProperties({
    brand: BRAND,
    subscription_status: mapSubscriptionStatusToTaxonomy(
      subscriptionState,
      !!account?.id,
      isTrialing
    ),
    cancels_at_period_end: cancelsAtPeriodEnd(subscriptionState),
    n_children: eligibleChildren.length,
  });

  useEffect(() => {
    if (!client || !trustedEmail) return;
    const currentUser = client.getContext().user;
    if (currentUser?.email === trustedEmail) return;
    client.updateUserSync({ ...currentUser, email: trustedEmail });
  }, [client, trustedEmail]);

  return null;
}
